import { type } from 'arktype';
import { type ExperienceFormat } from '../experiences';

/**
 * Los cinco recordatorios de seguridad del documento 04, §7.3. Viajan siempre como CLAVE
 * i18n (namespace `safetyTips`), nunca como texto: la API nunca devuelve texto humano
 * (RNF-03, AGENTS.md) y es el frontend quien redacta.
 *
 * Los textos que hoy pueblan `src/frontend/src/i18n/{es,en}/safetyTips.json` son literales
 * del §7.3 y **provisionales**: el §4.2 excluye del alcance «redactar el contenido de los
 * consejos de seguridad» y D-26 (`specs/iteracion-04-enlace-organizador/decisiones.md`) sigue
 * BLOQUEADA a la espera del texto definitivo del cliente. Esta marca vive aquí y no en el
 * propio JSON porque JSON no admite comentarios; ampliar o sustituir esos textos es tarea de
 * ese namespace i18n, no de este contrato ni del módulo `safetyTips` del backend, que solo
 * mueven claves.
 */
export const SAFETY_TIP_KEYS = ['rutaDeSierra', 'acampadaOVivac', 'agua', 'nieve', 'eventoUrbano'] as const;
export const safetyTipKeySchema = type.enumerated(...SAFETY_TIP_KEYS);
export type SafetyTipKey = typeof safetyTipKeySchema.infer;

/** La respuesta del endpoint que calcula los recordatorios aplicables: un array de claves. */
export const safetyTipsResponseSchema = safetyTipKeySchema.array();
export type SafetyTipsResponse = typeof safetyTipsResponseSchema.infer;

/**
 * Lo que necesita `recordatoriosAplicables` para decidir: la categoría y el formato ya
 * elegidos en el plan. `categorySlug` es el slug real del catálogo `category` (sembrado en
 * `supabase/seed.sql`: `tierra`, `agua`, `aire`, `nieve`, `fitness`, `hobbies`) — no hay un
 * enumerado ArkType para ese catálogo (es una tabla con filas, no un tipo cerrado, ver
 * `contracts/categories`), así que aquí se recibe como `string` sin más.
 */
export interface EntradaSafetyTips {
	categorySlug: string;
	format: ExperienceFormat;
}

/**
 * Traducción propia de los "tipos de plan" que nombra el documento 04 §7.3 a nuestro modelo
 * real de categoría + formato — el PDF no los hace coincidir uno a uno, así que este mapeo es
 * una decisión del proyecto, no una transcripción literal del documento:
 * - `rutaDeSierra` ← categoría `tierra` (rutas de sierra son el grueso de esa categoría).
 * - `acampadaOVivac` ← formato `ACAMPADA`.
 * - `agua` ← categoría `agua`.
 * - `nieve` ← categoría `nieve`.
 * - `eventoUrbano` ← formato `TALLER` (los talleres del catálogo son, hoy, los planes que se
 *   celebran en entorno urbano).
 * Un mismo plan puede disparar más de un recordatorio a la vez (p. ej. una acampada en la
 * categoría `nieve` dispara `nieve` y `acampadaOVivac`); el orden del resultado sigue siempre
 * el de `SAFETY_TIP_KEYS`.
 */
export const recordatoriosAplicables = (entrada: EntradaSafetyTips): SafetyTipKey[] => {
	const aplicables: SafetyTipKey[] = [];
	if (entrada.categorySlug === 'tierra') {
		aplicables.push('rutaDeSierra');
	}
	if (entrada.format === 'ACAMPADA') {
		aplicables.push('acampadaOVivac');
	}
	if (entrada.categorySlug === 'agua') {
		aplicables.push('agua');
	}
	if (entrada.categorySlug === 'nieve') {
		aplicables.push('nieve');
	}
	if (entrada.format === 'TALLER') {
		aplicables.push('eventoUrbano');
	}
	return aplicables;
};
